import React, { useState, useCallback, memo, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { CiUser } from 'react-icons/ci';
import { useAuthStore } from '@/store/index';
import { lighten } from '../../lib/polished';
import { paths } from '@/shared/config/paths';
import { headerMenu } from '@/shared/config/viewConfig';
import theme from '@/styles/theme';
import PasswordChangeModal from '@/shared/components/modal/PasswordChangeModal';
import GnbMenuModal from '@/shared/components/modal/GnbMenuModal';

type NavMenuItemProps = {
	item: { dataText: string; pathname: string };
	pathSegment: string;
};

const NavMenuItem = memo<NavMenuItemProps>(({ item, pathSegment }) => {
	const navigate = useNavigate();

	const handleClick = useCallback(() => {
		navigate(item.pathname);
	}, [item.pathname]);

	const isActive = useMemo(() => {
		switch (item.dataText) {
			case '대시보드':
				return pathSegment.includes('dashboard') || pathSegment === '';
			case '메인섹션':
				return pathSegment.includes('main');
			case '회원':
				return pathSegment.includes('user');
			case '대회':
				return pathSegment.includes('contest');
			case '프로그램':
				return pathSegment.includes('program');
			case '운영':
				return pathSegment.includes('operation');
			case '리셉션':
				return pathSegment.includes('reception');
			case '결제':
				return pathSegment.includes('payments'); // TODO : 결제·주문 관리 경로 추가
			default:
				return false;
		}
	}, [item.dataText, pathSegment]);

	return (
		<MenuItem>
			<MenuText data-text={item.dataText} $isActive={isActive} onClick={handleClick}>
				{item.dataText}
			</MenuText>
		</MenuItem>
	);
});

const Header = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { admin } = useAuthStore();
	const [menuModal, setMenuModal] = useState(false);

	const pathSegment = useMemo(() => pathname.split('/')[1] || '', [pathname]);

	return (
		<Wrapper>
			<Contents>
				<Box>
					<LogoTitle onClick={() => navigate(paths.home.path)}>ROCK & RUN ADMIN</LogoTitle>
					<MenuList>
						{headerMenu.map((list, index) => (
							<NavMenuItem key={`${index}-tab-${list.dataText}`} item={list} pathSegment={pathSegment} />
						))}
					</MenuList>
				</Box>
				<Box>
					{admin?.grade === 'MASTER' ? ( // 마스터 관리자 여부
						<>
							<MasterText onClick={() => navigate(paths.system.admin.list.path)}>설정</MasterText>
							<MasterIcon onClick={() => setMenuModal(true)}>M</MasterIcon>
						</>
					) : (
						<UserIcon onClick={() => setMenuModal(true)}>
							<CiUser fontSize={19} />
						</UserIcon>
					)}
					<GnbMenuModal menuModal={menuModal} setMenuModal={setMenuModal} />
				</Box>
			</Contents>
			{admin?.passwordChangeRequired && <PasswordChangeModal isInit={admin?.passwordChangeRequired} />}
		</Wrapper>
	);
};

export default memo(Header);

const Wrapper = styled.header`
	position: sticky;
	top: 0;
	z-index: 5;
	border-bottom: 1px solid ${theme.colors.ultraLightGrayBorder};
	background-color: ${theme.colors.whiteColor};
`;
const Contents = styled.div`
	width: 1280px;
	height: 80px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin: 0 auto;
`;
const Box = styled.div`
	display: flex;
	align-items: center;
	position: relative;
`;
const LogoTitle = styled.span`
	font-size: 16px;
	font-family: PretendardMedium, AppleSDGothicNeoMedium, sans-serif;
	padding-top: 2px;
	margin-right: 35px;
	cursor: pointer;
`;
const MenuList = styled.ul`
	font-size: 14px;
	display: flex;
	align-items: center;
`;
const MenuItem = styled.li`
	margin-right: 20px;
	cursor: pointer;
`;
const MenuText = styled.div<{ $isActive?: boolean }>`
	position: relative;
	visibility: hidden;
	font-family: PretendardRegular, AppleSDGothicNeoRegular, sans-serif;
	padding: 0 2px;
	color: ${theme.colors.blackColor};
	transition: opacity 0.25s ease-in-out;
	display: inline-block;

	&::before {
		content: attr(data-text);
		visibility: visible;
		position: absolute;
		top: 0;
		left: 0;
		opacity: 1;
	}

	&::after {
		content: attr(data-text);
		visibility: visible;
		font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0;
	}

	&:hover {
		&::before {
			opacity: 0;
		}

		&::after {
			opacity: 1;
		}
	}

	${({ $isActive }) =>
		$isActive &&
		css`
			&::before {
				opacity: 0;
			}

			&::after {
				opacity: 1;
			}
		`};
`;
const MasterText = styled.div`
	font-family: AppleSDGothicNeoRegular, sans-serif;
	font-size: 14px;
	margin-right: 20px;
	cursor: pointer;

	&:hover {
		font-family: AppleSDGothicNeoBold, sans-serif;
	}
`;
const MasterIcon = styled.div`
	width: 32px;
	height: 32px;
	font-size: 18px;
	color: ${theme.colors.whiteColor};
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	transition: 0.2s;
	background-color: ${theme.colors.blackColor};
	cursor: pointer;

	&:hover {
		background-color: ${lighten(0.35, theme.colors.blackColor)};
	}
`;
const UserIcon = styled.div`
	width: 32px;
	height: 32px;
	color: ${theme.colors.grayIconColor};
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${theme.colors.lightGrayBorder};
	border-radius: 4px;
	transition: 0.25s;
	cursor: pointer;

	&:hover {
		background-color: ${theme.colors.lightGrayHoverColor};
	}
`;
const Logout = styled.button`
	margin-left: 20px;
	cursor: pointer;

	&:hover:not(:disabled) {
		color: ${theme.colors.activeOrange};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;
